import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { ClassDurationsDataService, ClassDurationsColumnsService } from '../../services';
import { DialogClassDurationFormComponent } from './class-duration-form/class-duration-form.component';
import { DialogClassDurationDetailsComponent } from './class-duration-details/class-duration-details.component';
import { ClassDuration, ClassDurationsResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-class-durations',
  templateUrl: './class-durations.component.html',
  styleUrls: ['./class-durations.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class ClassDurationsComponent implements OnInit, OnDestroy {

  private classDurationAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private classDurationEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private classDurationDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public classDurationToDelete: ClassDuration = {};
  public classDurations: ClassDuration[] = [];
  public classDuration: ClassDuration = {};
  public selectedClassDurations: ClassDuration[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private classDurationsDataService: ClassDurationsDataService,
    private classDurationsColumnsService: ClassDurationsColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('ClassDurationsComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Class Durations - Management');
    this.primengConfig.ripple = true;
    this.getClassDurations();
  }

  ngOnDestroy(): void {
    console.log('ClassDurationsComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getClassDurations(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.classDurationsDataService.getClassDurations(this.parameters).subscribe((response: ClassDurationsResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.classDurations = response.results;
        } else {
          this.classDurations = this.classDurations.concat(response.results);
        }
        if (response.inlineCount > this.classDurations.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getClassDurations();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CLASSDURATIONSNOTLOADED')});
    });
  }

  importClassDurations(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.classDurationsDataService.addClassDurations(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('CLASSDURATIONSCREATED')});
                if (proceed === j) {
                  this.getClassDurations();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CLASSDURATIONSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CLASSDURATIONSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.classDurationsDataService.updateClassDurations(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('CLASSDURATIONSUPDATED')});
                if (proceed === j) {
                  this.getClassDurations();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CLASSDURATIONSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CLASSDURATIONSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.classDurationsColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('classDurations.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedClassDurations() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Class Durations?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.classDurationsDataService.deleteClassDurations(this.selectedClassDurations).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Class Durations Deleted', life: 3000});
            this.getClassDurations();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Class Durations not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Class Durations not deleted'});
        });
        this.selectedClassDurations = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.classDurations, this.columns, 'ClassDurations');
  }

  exportExcel() {
    ToolsService.exportExcel(this.classDurations, this.columns, 'ClassDurations');
  }

  exportCsv() {
    ToolsService.exportCsv(this.classDurations, this.columns, 'ClassDurations');
  }

  refresh() {
    this.classDurationsDataService.clearCache();
    this.getClassDurations();
  }

  classDurationAdd(): void {
    if (!this.navigation.getParamValue('classDurations.addInDialog', true)) {
      this.router.navigate(['/class-durations/add']);
      return;
    }
    this.classDurationAddDialog = this.dialogService.open(DialogClassDurationFormComponent, {
      header: '', // this.translate.instant('NEWCLASSDURATION'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        classDuration: {id: null},
        users: this.classDurationsColumnsService.users,
      },
    });

    this.classDurationAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'ClassDuration added'});
        this.getClassDurations();
      }
    });
  }

  classDurationEdit(classDuration: ClassDuration): void {
    if (!this.navigation.getParamValue('classDurations.editInDialog', true)) {
      this.router.navigate(['/class-durations/edit/' + classDuration.id]);
      return;
    }
    this.classDurationEditDialog = this.dialogService.open(DialogClassDurationFormComponent, {
      header: '', // this.translate.instant('EDITCLASSDURATION') + ' ' + classDuration.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        classDuration: classDuration,
        users: this.classDurationsColumnsService.users,
      },
    });

    this.classDurationEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'ClassDuration updated'});
        this.getClassDurations();
      }
    });
  }

  classDurationDetails(classDuration: ClassDuration): void {
    if (!this.navigation.getParamValue('classDurations.detailsInDialog', true)) {
      this.router.navigate(['/class-durations/details/' + classDuration.id]);
      return;
    }
    this.classDurationDetailsDialog = this.dialogService.open(DialogClassDurationDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        classDuration: classDuration,
      }
    });
  }

  classDurationDelete(classDuration: ClassDuration): void {
    this.classDurationToDelete = classDuration;
    this.messageService.clear();
    this.messageService.add({
      key: 'classDurationDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + classDuration.name + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.classDurationsDataService.deleteClassDuration(this.classDurationToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Class Duration deleted'});
      this.getClassDurations();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Class Duration not deleted'});
    });
  }

  onDeleteReject(): void {
    this.classDurationToDelete = {};
    this.messageService.clear('classDurationDelete');
  }

}
